import {Injectable} from '@angular/core';
import {SocketMessagesService} from '../core/socket/socket.service';
import {BehaviorSubject, combineLatest, EMPTY, Observable, of, OperatorFunction, Subject} from 'rxjs';
import {IBillingEntity, IBookableEntity, IBookingTypesList, ICapabilities, ICaptionSource, IOrganization} from '../types';
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    pluck,
    shareReplay,
    startWith,
    switchMap,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import {AuthService} from '../core/auth.service';
import {CommonService} from './common.service';
import {CustomErrorHandlerService} from '../core/custom-error-handler.service';
import { BOOKING_LIST } from '../constants';
import { AudioSourcesService } from './audio-sources.service';
import { DisplayCaptionsService } from './display-captions.service';
import { isApiEnabled } from './utils';

@Injectable({
    providedIn: 'root'
})
export class CapabilitiesService {
    public caption_options!: any
    public audio_options!: any
    private reloadCapabilities$$: Subject<void> = new Subject<void>();
    private capabilities$$: Subject<ICapabilities> = new Subject<ICapabilities>();
    private readonly capabilities$: Observable<ICapabilities> = this.capabilities$$.asObservable().pipe(
        filter(Boolean),
        map(_ => _ as ICapabilities),
        shareReplay(1)
    );
    private reloadBookableEntities$$: Subject<void> = new Subject<void>();
    private bookableEntities$$: BehaviorSubject<IBookableEntity[] | void> = new BehaviorSubject<IBookableEntity[] | void>(void 0);
    private readonly bookableEntities$: Observable<IBookableEntity[]> = this.bookableEntities$$.asObservable().pipe(
        filter(Boolean),
        map(_ => _ as IBookableEntity[]),
        shareReplay(1)
    );
    public currentBillingEntityId$$: BehaviorSubject<number> = new BehaviorSubject<number>(this.auth.getUserBillingEntityId());
    private currentOrgId$$: BehaviorSubject<number> = new BehaviorSubject<number>(this.auth.getUserOrgId());
    public current_be!: IBillingEntity
    public canCreateInstantLexi$$ = new Subject<boolean>()
    public smartLexi$$ = new Subject<any>()
    public instantLexi$$ = new Subject<any>()
    public service_types_list: any = []
    public default_service_type: string = 'Remote Captioning'
    public booking_creation_types_list: any = []


    public currentBillingEntityId$: Observable<number> = this.currentBillingEntityId$$.asObservable().pipe(
        filter(Boolean),
        map(_ => parseInt(_ as unknown as string)),
        distinctUntilChanged(),
    );
    public currentOrgId$: Observable<number> = this.currentOrgId$$.asObservable().pipe(
        filter(Boolean),
        map(_ => parseInt(_ as unknown as string)),
        distinctUntilChanged(),
    );

    constructor(
        private messages: SocketMessagesService,
        private error: CustomErrorHandlerService,
        private auth: AuthService,
        private common: CommonService,
        private audioSources: AudioSourcesService,
        private display: DisplayCaptionsService,
    ) {
        this.canCreateInstantLexi$$.next(false)
        this.smartLexi$$.next({})
        this.instantLexi$$.next({})

        this.updateLexiServiceTypes()

        setTimeout(() => {
            this.currentBillingEntityId$$.pipe(tap(billing_entity_id => {
                this.common.getBillingEntitiesMap$().pipe(
                    map((entities) => {
                        return entities.get(billing_entity_id as number) as IBillingEntity;
                    }),
                    tap((be) => {
                        this.current_be = be
                        this.auth.cached_billing_entity = be
                        this.updateBookingTypes(be)
                    }),
                ).subscribe();
            })).subscribe()
        }, 300)

        // LBP-360: catchError used to sit on this outer pipe, subscribed once here and never
        // again - its EMPTY fallback completed this single continuous subscription the first
        // time getBookableEntities ever errored, permanently killing every future reload (BE
        // switch, retry, etc.) for the rest of the session, with only the one error toast ever
        // shown. Scoping it inside switchMap's inner observable instead means an error only ends
        // the current cycle; switchMap moves on to the next reloadBookableEntities$$ emission
        // normally.
        this.reloadBookableEntities$$.asObservable().pipe(
            // @ts-ignore
            switchMap(() => this.messages.request$<{ bookable_entities: IBookableEntity[] }>('getBookableEntities').pipe(
                pluck('bookable_entities') as OperatorFunction<unknown, void | IBookableEntity[]>,
                tap(this.bookableEntities$$.next.bind(this.bookableEntities$$)),
                catchError(err => {
                    this.error.handleError(err, '@capabilities.service1');
                    return EMPTY;
                })
            )),
        ).subscribe();

        this.display.captions$.subscribe((captions: ICaptionSource[]) => {
            const org_id = this.auth.getUserInfo()?.org_id;
            if (org_id == null) {
                return;
            }
            let caption_options: any = []
            captions.forEach((i: any) => {
                // @ts-ignore
                const { caption_output_id, caption_output_name, caption_params, organisations } = i
                if (organisations.includes(org_id)) {
                    caption_options = [
                        ...caption_options,
                        {
                            caption_output_id,
                            caption_output_name,
                            caption_params: caption_params.map((__: any) => {
                                const {caption_param_id} = __;
                                return {caption_param_id, value: ''};
                            }),
                            destination_languages: '',
                            machine_translation: undefined
                        }
                    ]
                }
            })
            this.caption_options = caption_options
        })

        this.reloadCapabilities$$.asObservable().pipe(
            // @ts-ignore
            switchMap(() => this.messages.request$<ICapabilities>('getCapabilities')),
            tap(this.capabilities$$.next.bind(this.capabilities$$)),
            catchError(err => {
                this.error.handleError(err, '@capabilities.service2');
                return EMPTY;
            })
        ).subscribe();

        this.audioSources.audio$.pipe(
            tap((sources) => {
                const org_id = this.auth.getUserInfo()?.org_id;
                if (org_id == null) {
                    return;
                }
                let sources_options: any = [];
                sources.forEach((i: any) => {
                    // @ts-ignore
                    const { audio_input_id, audio_name, audio_params, organisations } = i;
                    if (organisations.includes(org_id)) {
                        sources_options = [
                            ...sources_options,
                            {
                                audio_input_id,
                                audio_name,
                                audio_params,
                            },
                        ];
                    }
                });
                this.audio_options = sources_options;
            })
        ).subscribe();

        this.auth.isLoggedIn$.pipe(
            distinctUntilChanged(),
            tap((isLoggedIn) => {
                if (isLoggedIn) {
                    this.reloadBookableEntities();
                    const sessionOrgId = this.auth.curr_org_id || this.auth.getUserOrgId();
                    const sessionBeId = this.auth.curr_billing_entity_id || this.auth.getUserBillingEntityId();
                    this.changeOrg(sessionOrgId);
                    this.changeBE(sessionBeId);
                    this.reloadCapabilities();
                }
            })
        ).subscribe();
    }

    public getCurrentBillingEntity$() {

    }

    public updateBookingTypes(be: IBillingEntity) {
        const service_types = this.auth
            .getServiceTypesList()
            .filter((st: any) => be?.service_types.includes(st.id))

        const booking_creation_types = this.auth
            .getBookingCreationTypesList()
            .filter((bct: any) => be?.booking_creation_types?.includes(bct.id))

        this.service_types_list = service_types
        this.booking_creation_types_list = booking_creation_types
        this.default_service_type = <string>this.current_be?.default_service_type || ''

        let lexi_service_types: any = []

        service_types?.forEach((o: any) => {
            if (['inl', 'il', 'sm', 'lsl'].includes(o.srv_id)) {
                lexi_service_types.push(o)
            }

            if (o.srv_id === 'inl') {
                this.canCreateInstantLexi$$.next(true)
            }
        })

        if (lexi_service_types?.length) {
            this.instantLexi$$.next(lexi_service_types)
        }

    }

    updateLexiServiceTypes() {
        let lexi_service_types: any = []
        const service_types = this.auth
            .getServiceTypesList()

        service_types?.forEach((o: any) => {
            if (['inl', 'il', 'sm', 'lsl'].includes(o.srv_id)) {
                lexi_service_types.push(o)
            }
        })
    }

    public isSysAdmin$(): Observable<boolean> {
        return this.capabilities$.pipe(
            map(_ => Boolean(_.sys_admin_group))
        );
    }

    /** LBP-108: the restricted internal tier - sitewide parity with sys_admin on everything except Globals/Tools/API docs. */
    public isOpsGroup$(): Observable<boolean> {
        return this.capabilities$.pipe(
            map(_ => Boolean(_.ops_group))
        );
    }

    public isReadOnly$(): Observable<boolean> {
        return this.capabilities$.pipe(
            map(_ => Boolean(_.read_only_user))
        );
    }

    public isAdmin$(): Observable<boolean> {
        return this.capabilities$.pipe(
            map(_ => Boolean(_.admin_group))
        );
    }

    private showApiDocsNav$$?: Observable<boolean>;

    /**
     * API docs nav link: sys admins, account admins and developers, only while the account's API switch is on.
     * Sys admins follow the selected account; everyone else their own, the same account the backend checks.
     * Shared/replayed so the nav link and {@link ApiDocsGuard} (both call this) see the same answer from the
     * same request, instead of each racing its own `getBillingEntities` round trip - on a slow/flaky connection
     * that let the nav link show while the guard's own call dropped and redirected away.
     */
    public showApiDocsNav$(): Observable<boolean> {
        const { sys_admin, admin_group, developer, billing_entity_id } = this.auth.getUserInfo() || ({} as any);
        if (!(sys_admin || admin_group || developer)) return of(false);
        if (!this.showApiDocsNav$$) {
            const account$ = sys_admin ? this.currentBillingEntityId$ : of(Number(billing_entity_id));
            this.showApiDocsNav$$ = account$.pipe(
                switchMap((id) => this.messages.request$('getBillingEntities', { billing_entity_id: id }).pipe(
                    // admins get their whole org back, so pick the account by id
                    map((data: any) => isApiEnabled(data?.billing_entities?.find((b: IBillingEntity) => Number(b.billing_entity_id) === id)?.settings)),
                    catchError(() => of(false)),
                )),
                distinctUntilChanged(),
                shareReplay({ bufferSize: 1, refCount: true }),
            );
        }
        return this.showApiDocsNav$$;
    }

    /**
     * Settings nav link visibility. Matches {@link SettingsGuard} access rules (`sys_admin_group` or
     * `ops_group` — `admin_group` is a client-org-admin flag and must not grant Settings visibility)
     * but emits immediately when login storage has `sys_admin`/`ops_group`, without waiting for
     * `getCapabilities` (avoids missing nav until refresh).
     */
    public showSettingsNav$(): Observable<boolean> {
        const storedUser = this.auth.getUserInfo();
        const storedAccess = Boolean(storedUser?.sys_admin || storedUser?.ops_group);
        return combineLatest([
            of(storedAccess),
            this.isSysAdmin$().pipe(startWith(false)),
            this.isOpsGroup$().pipe(startWith(false)),
        ]).pipe(
            map(([stored, isSysAdmin, isOpsGroup]) => stored || isSysAdmin || isOpsGroup),
            distinctUntilChanged(),
        );
    }

    /** LBP-108: fail-closed - hidden/blocked unless the backend confirms the show_support_page config flag is on. */
    public showSupportPage$(): Observable<boolean> {
        return this.capabilities$.pipe(
            map(_ => Boolean(_.show_support_page))
        );
    }

    /** LBP-361 Phase 2: restricted to exactly these two people, not a role/group. */
    private static readonly LOG_VIEWER_ALLOWED_EMAILS = ['darwayne.alfaro@ai-media.tv', 'eric.kale@ai-media.tv'];

    /**
     * Single source of truth for log-viewer access - {@link LogViewerToolGuard} and the Tools tab
     * list must both derive from this, not separate checks (this is the specific mistake LBP-346
     * made for Settings: nav visibility and the route guard drifted onto different conditions).
     */
    public isLogViewerAllowed(): boolean {
        const email = (this.auth.getUserInfo()?.email || '').toLowerCase();
        return CapabilitiesService.LOG_VIEWER_ALLOWED_EMAILS.includes(email);
    }

    /** Personal admin tool, independent of {@link LOG_VIEWER_ALLOWED_EMAILS} - restricted to Darwayne only. */
    private static readonly CONFIG_TOOL_ALLOWED_EMAILS = ['darwayne.alfaro@ai-media.tv'];

    /**
     * Single source of truth for the Config tool's access - {@link ConfigToolGuard} and the Tools tab
     * list must both derive from this, not separate checks (same discipline as {@link isLogViewerAllowed}).
     */
    public isConfigToolAllowed(): boolean {
        const email = (this.auth.getUserInfo()?.email || '').toLowerCase();
        return CapabilitiesService.CONFIG_TOOL_ALLOWED_EMAILS.includes(email);
    }

    getBookableEntities$(): Observable<IBookableEntity[]> {
        return this.bookableEntities$.pipe(
        );
    }


    // @todo return Billing entity object
    public getBillingEntity$(): Observable<number> {
        return this.capabilities$.pipe(
            map(_ => _.billing_entity_id)
        );
    }

    // @todo return Organization object
    public getOrganization$(): Observable<IOrganization> {
        return this.currentOrgId$.pipe(
            withLatestFrom(this.common.getOrganizationsMap$(), (org_id, organizationMap) => {
                return organizationMap.get(org_id) as IOrganization;
            })
        );
    }

    public canCreateBookingInAnyOrg$(): Observable<boolean> {
        return this.capabilities$.pipe(
            map(_ => _.create_booking_in_any_org)
        );
    }

    public canCreateBookingInCurrentOrg$(): Observable<boolean> {
        return this.capabilities$.pipe(
            map(_ => _.create_booking_in_org)
        );
    }

    public canCreateBooking$(): Observable<boolean> {
        return this.capabilities$.pipe(
            map(_ => _.create_booking_in_be)
        );
    }

    public reloadBookableEntities(): void {
        this.reloadBookableEntities$$.next();
    }

    public reloadCapabilities(): void {
        this.reloadCapabilities$$.next();
    }

    public changeOrg(id: number): void {
        this.currentOrgId$$.next(id);
    }

    public changeBE(id: number): void {
        this.currentBillingEntityId$$.next(id);
    }

    public getBookingTypes$(): Observable<IBookingTypesList[]> {
        return this.currentOrgId$.pipe(
            switchMap(currentOrgId => {
                return this.common.getOrganizationsMap$().pipe(
                    filter(_ => !!_.size),
                    map(organizations => {
                        return organizations.get(currentOrgId) as IOrganization;
                    })
                );
            }),
            map(() => {
                return BOOKING_LIST.filter((list: IBookingTypesList) => {
                    return this?.booking_creation_types_list
                        ?.some((i: any) => i.short_id === list.value)
                });
            })
        );
    }

    getCurrentBillingEntity = () => {


    }
}
